import {
  Add as AddIcon,
  AutoAwesomeMosaic as AutoAwesomeMosaicIcon,
  SubdirectoryArrowRight as SubdirectoryArrowRightIcon,
} from '@mui/icons-material';
import { Alert, Box, Button } from '@mui/material';
import dayjs from 'dayjs';
import {
  concat,
  debounce,
  isEmpty,
  isNumber,
  toNumber,
  toString,
} from 'lodash';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormProvider } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { AssemblyLayout } from 'src/components/assemblyLayout';
import { AuthDataProvider } from 'src/components/authProvider';
import { Breadcrumbs } from 'src/components/breadcrumbs';
import { CustomerStateAlert } from 'src/components/customerStateAlert/CustomerStateAlert';
import { FullSizeAssemblyScreen } from 'src/components/fullSizeAssemblyScreen';
import { FullSizeAssemblySection } from 'src/components/fullSizeAssemblySection/FullSizeAssemblySection';
import {
  ComponentListModal,
  CreateVariationModal,
  SimpleDialog,
} from 'src/components/modal';
import { Progress } from 'src/components/progress/Progress';
import { queryClient } from 'src/components/queryProvider/QueryProvider';
import { ScaleContainer } from 'src/components/scaleContainer/ScaleContainer';
import { MiniSidebar } from 'src/components/sidebar';
import { Snackbar } from 'src/components/snackbar';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { ViewOnlyAlert } from 'src/components/viewOnlyAlert';
import {
  BACKUP_ASSEMBLY_COMPONENT_CONFIG,
  COLLECTIONS,
  DEFAULT_ASSEMBLY_COMPONENT_CONFIG,
  MAX_ALLOWED_COMPONENTS,
  USER_ACTIONS,
} from 'src/constants/constants';
import { PATHS } from 'src/constants/paths';
import { StyledWarningIcon } from 'src/features/components/editComponentPage/forms/FormWrapper.styled';
import { narrativeGenerators } from 'src/hooks/api/userActions/helper';
import { AuthClassification } from 'src/hooks/auth/auth.interface';
import { useNavigateBlocker } from 'src/hooks/navigation';
import {
  ComponentTypes,
  IAssembly,
  IAssemblyComponent,
  IComponent,
  ISectionConfig,
  IStatus,
} from 'src/interfaces';
import {
  findMatchedGlobalComponents,
  generateAssemblyProductFilters,
  isDisallowedOutdoorSection,
  lang,
  queryKeys,
  useUserRole,
} from 'src/utils';
import {
  FileTypes,
  getBackgroundVideoDuration,
  getFileExtensionType,
} from 'src/utils/file-types';
import {
  assemblyAddComponentEvent,
  assemblySavedDraftEvent,
  replaceComponentWithinAssemblyEvent,
  saveReadyToUseAssemblyEvent,
} from 'src/utils/google-tag-manager';
import { checkSectionTypes } from 'src/utils/section-types-helper';
import { uid } from 'uid';

import {
  findMatchedAssemblyComponents,
  getAssemblyExpiryInfo,
  getUpdatedAssemblyValues,
  handleFiltersForComponents,
  showChildIcon,
  showParentIcon,
} from './EditAssemblyPage.helper';
import {
  ActionButtonsContainer,
  AssemblyEditContainer,
  AssemblyEditContentContainer,
  AssemblyNameText,
  HeaderContainer,
  SidebarContainer,
} from './EditAssemblyPage.styled';
import { useEditAssemblyPage } from './hooks/EditAssemblyPage.hook';
import { AssemblyEditPageSidebar } from './modules/AssemblyEditPageSidebar';
import { DurationUpdateTypes } from './modules/sectionDetailsSidebar/components/componentInfo/ComponentInfo.interface';
let variationAssemblies: IAssembly[] = [];

// TODO: split this fille, no file should be more than 300 lines.
export const EditAssemblyPage = () => {
  const navigate = useNavigate();
  const { isViewOnly } = useUserRole();
  const [childSectionEdit, setChildSectionEdit] = React.useState(false);
  const [isCodConfirmationOpen, setIsCodConfirmationOpen] = useState<
    boolean | undefined
  >(undefined);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);

  const { assemblyId = '' } = useParams();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const replaceComponentID = React.useRef<number>(0);
  const currentDate = dayjs().add(1, 'day').format('YYYY-MM-DD');
  const {
    replaceAssemblyComponentId,
    openSaveComponentModal,
    openComponentListModal,
    openSectionDrawer,
    openCreateVariationModal,
    assemblyTransaction,
    createAssemblyVariation,
    canSaveAsDraft,
    canSaveAsReady,
    disableRemoveVariation,
    canEdit,
    parentAssembly,
    parentAssemblyComponents,
    parentBackupAssemblyComponents,
    isParentAssemblyReady,
    formMethods,
    formFieldArray,
    backupFieldArray,
    layouts,
    selectedSection,
    isAssemblyDetailsLoading,
    componentTypes,
    onToggleSaveComponentModal,
    onChangeSelectedSection,
    onReplacedAssemblyComponent,
    onToggleComponentListModal,
    onToggleSectionDrawer,
    onToggleCreateVariationModal,
    activeAssemblyJourney,
    setActiveAssemblyJourney,
    customerState,
    setCustomerState,
    shouldBlock,
    globalComponentsData,
    onChangeDaypart,
    selectedDaypartId,
  } = useEditAssemblyPage(assemblyId);
  const isDirty = formMethods.formState.isDirty;

  const [assemblyName, assemblyComponents] = formMethods.watch([
    'name',
    'assemblyComponents',
  ]);

  const { backupAssemblyComponents = [] } = formMethods.getValues();
  useNavigateBlocker(shouldBlock && !isSaveDisabled);
  const { userRole } = useContext(AuthDataProvider);
  const handleChildEditSection = (childSectionEditVal: boolean) => {
    setChildSectionEdit(childSectionEditVal);
  };

  const sectionTypes = formMethods.getValues('layout.config.sectionTypes');
  const { hasJourney } = checkSectionTypes(sectionTypes);

  const handleComponentSelect = (
    layoutScreenId: number,
    screenSectionId: number,
    sectionId: number,
    width: number,
    height: number,
    config?: ISectionConfig,
    childSectionEditVal?: boolean,
  ) => {
    onChangeSelectedSection({
      layoutScreenId,
      screenSectionId,
      sectionId,
      width,
      height,
      config,
    });

    const component = formMethods
      .getValues()
      .assemblyComponents.find(
        (item) =>
          item.layout_screen_id === layoutScreenId &&
          item.screen_section_id === screenSectionId,
      );
    if (!component) {
      replaceComponentID.current = 0;
    }

    if (
      component ||
      (formMethods.getValues()?.variation_parent_id !== null &&
        !childSectionEditVal)
    ) {
      onToggleSectionDrawer(true);
    } else {
      onToggleComponentListModal(true);
    }
  };

  const components = useMemo(
    () => formMethods.getValues()?.components || [],
    [formMethods],
  );

  // Expiry messaging (save toast + save button state) must reflect the
  // components currently on the form, not the mount-time snapshot held by the
  // memo above (which is kept stable for the product filters). Watching the
  // field ensures added/replaced components are surfaced correctly.
  const currentComponents = formMethods.watch('components') ?? [];

  const { isExpired, expiringInDays } =
    getAssemblyExpiryInfo(currentComponents);

  const onCloseComponentModal = () => {
    onReplacedAssemblyComponent(undefined);
    onToggleComponentListModal(false);
  };

  const onCloseSectionDrawer = () => {
    onReplacedAssemblyComponent(undefined);
    onChangeSelectedSection(undefined);
    onToggleSectionDrawer(false);
  };

  const onSelectBackupComponent = (
    component: IComponent,
    rotation_order: number,
    duration?: number,
  ) => {
    if (component) {
      const { backupAssemblyComponents } = formMethods.getValues();
      const selectedSectionBackupComponents = backupAssemblyComponents.filter(
        (item): item is IAssemblyComponent =>
          !!item &&
          item.layout_screen_id === selectedSection?.layoutScreenId &&
          item.screen_section_id === selectedSection?.screenSectionId,
      );
      // Prevent adding duplicate backup components due to potential race conditions or rapid user actions
      const isComponentAlreadyPresentInBackup =
        selectedSectionBackupComponents.find(
          (backupComponent) => backupComponent.component_id === component.id,
        );

      const currentRotationBackupComponents =
        selectedSectionBackupComponents.filter(
          (item) => item.rotation_order === rotation_order,
        );

      if (
        isComponentAlreadyPresentInBackup ||
        currentRotationBackupComponents.length >= MAX_ALLOWED_COMPONENTS
      ) {
        return;
      }

      const orderArray = selectedSectionBackupComponents
        .filter((item) => item.rotation_order === rotation_order)
        .map((component) => component.backup_order);
      backupFieldArray.append({
        uid: uid(),
        assembly_id: toNumber(assemblyId),
        layout_screen_id: selectedSection?.layoutScreenId ?? 0,
        screen_section_id: selectedSection?.screenSectionId ?? 0,
        component_id: component.id,
        component: component,
        status: IStatus.DRAFT,
        start_date: dayjs('1970-01-01').format('YYYY-MM-DD'),
        end_date: null,
        allow_overrides: false,
        backup_order: orderArray.length > 0 ? Math.max(...orderArray) + 1 : 1,
        config: {
          ...BACKUP_ASSEMBLY_COMPONENT_CONFIG,
          ...(duration && { duration }),
        },
        rotation_order: rotation_order,
      } as IAssemblyComponent);
    }
  };

  const onSelectedComponent = async (component?: IComponent) => {
    if (component) {
      let duration = DEFAULT_ASSEMBLY_COMPONENT_CONFIG.duration;
      const currentAssemblyComponents =
        formMethods.getValues().assemblyComponents;
      const assemblyComponentIndex = currentAssemblyComponents.findIndex(
        (item) => item.uid === replaceAssemblyComponentId,
      );

      if (
        component?.component_type?.name === ComponentTypes.PROMOTIONAL_SPACE
      ) {
        const componentBackgroundType = getFileExtensionType(
          component?.config?.background,
        );
        if (componentBackgroundType === FileTypes.VIDEO) {
          duration =
            (await getBackgroundVideoDuration(component?.config?.background)) *
            1000;
        }
      }

      if (assemblyComponentIndex !== -1) {
        const componentId =
          currentAssemblyComponents[assemblyComponentIndex].component?.id;

        const components = formMethods
          .getValues()
          .components.map((assemblyComponent) =>
            assemblyComponent.id === componentId
              ? component
              : assemblyComponent,
          );
        formMethods.setValue('components', components);
      }

      if (assemblyComponentIndex !== -1) {
        const assemblyComponent =
          currentAssemblyComponents[assemblyComponentIndex];
        const updatedComponents = [...currentAssemblyComponents];
        updatedComponents[assemblyComponentIndex] = {
          ...assemblyComponent,
          component_id: component.id,
          component: component,
          config: {
            ...assemblyComponent?.config,
            transition: assemblyComponent?.config?.transition ?? 'fade',
            duration,
          },
        };
        formMethods.setValue('assemblyComponents', updatedComponents);
      } else {
        const selectedSectionComponents = formMethods
          .getValues()
          .assemblyComponents.filter(
            (item): item is IAssemblyComponent =>
              !!item &&
              item.layout_screen_id === selectedSection?.layoutScreenId &&
              item.screen_section_id === selectedSection?.screenSectionId,
          );
        if (selectedSectionComponents.length >= MAX_ALLOWED_COMPONENTS) {
          return;
        }
        const orderArray = selectedSectionComponents.map(
          (component) => component.rotation_order,
        );

        formFieldArray.append({
          uid: uid(),
          assembly_id: toNumber(assemblyId),
          layout_screen_id: selectedSection?.layoutScreenId ?? 0,
          screen_section_id: selectedSection?.screenSectionId ?? 0,
          component_id: component.id,
          component: component,
          status: IStatus.DRAFT,
          start_date: dayjs('1970-01-01').format('YYYY-MM-DD'),
          end_date: null,
          allow_overrides: false,
          backup_order: 0,
          config: {
            transition: 'fade',
            duration,
          },
          rotation_order:
            orderArray.length > 0 ? Math.max(...orderArray) + 1 : 0,
        } as IAssemblyComponent);
      }

      const matchedComponents = findMatchedAssemblyComponents(
        parentAssemblyComponents,
        selectedSection?.layoutScreenId,
        selectedSection?.screenSectionId,
        ['rotation_order'],
      );

      const componentId = matchedComponents?.[0]?.component?.id ?? 0;

      const components = formMethods
        .getValues()
        .components.map((assemblyComponent) =>
          assemblyComponent.id === componentId ? component : assemblyComponent,
        );

      if (replaceComponentID?.current > 0) {
        replaceComponentWithinAssemblyEvent(
          assemblyId,
          replaceComponentID.current,
          component.id,
        );
      } else {
        assemblyAddComponentEvent(assemblyId, component?.id);
      }

      formMethods.setValue('components', components);

      onCloseComponentModal();
    }
  };

  const debouncedSelectComponent = debounce((component?: IComponent) => {
    onSelectedComponent(component);
  }, 500);

  const onDeletedAssemblyComponent = (
    assemblyComponent: IAssemblyComponent,
  ) => {
    // Delete the corresponding backup assembly component and adjust rotation orders.
    const updatedBackupAssemblyComponents = formMethods
      .getValues()
      .backupAssemblyComponents.filter(
        (item) =>
          item.rotation_order !== assemblyComponent.rotation_order ||
          item.layout_screen_id !== assemblyComponent.layout_screen_id ||
          item.screen_section_id !== assemblyComponent.screen_section_id,
      )
      .map((item) =>
        item.rotation_order !== undefined &&
        item?.rotation_order > assemblyComponent.rotation_order &&
        item.layout_screen_id === assemblyComponent.layout_screen_id &&
        item.screen_section_id === assemblyComponent.screen_section_id
          ? { ...item, rotation_order: item?.rotation_order - 1 }
          : item,
      );
    formMethods.setValue(
      'backupAssemblyComponents',
      updatedBackupAssemblyComponents,
    );

    const assemblyComponentArray = formMethods
      .getValues()
      .assemblyComponents.filter(
        (item) =>
          item.layout_screen_id === assemblyComponent.layout_screen_id &&
          item.screen_section_id === assemblyComponent.screen_section_id,
      );

    const extractAssemblyComponents = formMethods
      .getValues()
      .assemblyComponents.filter(
        (item) =>
          !(
            item.layout_screen_id === assemblyComponent.layout_screen_id &&
            item.screen_section_id === assemblyComponent.screen_section_id
          ),
      );

    if (assemblyComponentArray.length === 1) {
      const assemblyComponentIndex = formFieldArray.fields.findIndex(
        (item) => item.uid === assemblyComponent.uid,
      );
      formFieldArray.remove(assemblyComponentIndex);
      formMethods.setValue('assemblyComponents', extractAssemblyComponents);
    } else if (assemblyComponentArray.length > 1) {
      const updatedAssemblyComponentsArray = assemblyComponentArray
        .filter((item) => item.uid !== assemblyComponent.uid)
        .map((ac) => {
          if (ac.rotation_order > assemblyComponent.rotation_order) {
            return {
              ...ac,
              rotation_order: ac.rotation_order - 1,
            };
          }

          return ac;
        });

      const newAssemblyComponents = [
        ...extractAssemblyComponents,
        ...updatedAssemblyComponentsArray,
      ];

      formMethods.setValue('assemblyComponents', newAssemblyComponents);
    }
  };

  const onDeletedBackupAssemblyComponent = (
    backupAssemblyComponent: IAssemblyComponent,
  ) => {
    const updatedComponents = formMethods
      .getValues()
      .backupAssemblyComponents.filter(
        (component) => component.uid !== backupAssemblyComponent.uid,
      );
    formMethods.setValue('backupAssemblyComponents', updatedComponents);
  };

  const onClickedReplace = (
    assemblyComponentId: string,
    componentID: number,
  ) => {
    replaceComponentID.current = componentID;
    onReplacedAssemblyComponent(assemblyComponentId);
    onToggleComponentListModal();
  };

  const onClickedAdd = () => {
    replaceComponentID.current = 0;
    onToggleComponentListModal();
  };

  const onUpdateRotationDuration = useCallback(
    (uid: string, duration: number, type?: DurationUpdateTypes) => {
      const assembly = formMethods.getValues();
      const updateComponentConfig = (components?: IAssemblyComponent[]) =>
        components?.map((component) =>
          component.uid === uid
            ? { ...component, config: { ...component.config!, duration } }
            : component,
        );

      const updateBackupByRotationOrder = (rotationOrder?: number) =>
        isNumber(rotationOrder)
          ? assembly.backupAssemblyComponents?.map((component) =>
              component.rotation_order === rotationOrder
                ? { ...component, config: { ...component.config!, duration } }
                : component,
            )
          : assembly.backupAssemblyComponents;

      if (type === DurationUpdateTypes.BOTH) {
        const rotationOrder = assembly.assemblyComponents?.find(
          (component) => component.uid === uid,
        )?.rotation_order;

        const updatedAssemblyComponents = updateComponentConfig(
          assembly.assemblyComponents,
        );
        const updatedBackupAssemblyComponents =
          updateBackupByRotationOrder(rotationOrder);
        if (updatedAssemblyComponents) {
          formMethods.setValue('assemblyComponents', updatedAssemblyComponents);
        }
        if (updatedBackupAssemblyComponents) {
          formMethods.setValue(
            'backupAssemblyComponents',
            updatedBackupAssemblyComponents,
          );
        }

        return;
      }

      const key =
        type === DurationUpdateTypes.MAIN
          ? 'assemblyComponents'
          : 'backupAssemblyComponents';
      const updatedComponentConfig = updateComponentConfig(assembly[key]);
      if (updatedComponentConfig) {
        formMethods.setValue(key, updatedComponentConfig);
      }
    },
    [formMethods],
  );

  const onReorderRotation = (
    assemblyComponent: IAssemblyComponent,
    direction: 'up' | 'down',
  ) => {
    const assemblyComponents = formMethods
      .getValues()
      .assemblyComponents.filter(
        (item): item is IAssemblyComponent =>
          !!item &&
          item.layout_screen_id === assemblyComponent.layout_screen_id &&
          item.screen_section_id === assemblyComponent.screen_section_id,
      );

    const extractAssemblyComponents = formMethods
      .getValues()
      .assemblyComponents.filter(
        (item) =>
          !(
            item.layout_screen_id === assemblyComponent.layout_screen_id &&
            item.screen_section_id === assemblyComponent.screen_section_id
          ),
      );
    const backupAssemblyComponents = formMethods
      .getValues()
      .backupAssemblyComponents.filter(
        (item): item is IAssemblyComponent =>
          !!item &&
          item.layout_screen_id === assemblyComponent.layout_screen_id &&
          item.screen_section_id === assemblyComponent.screen_section_id,
      );

    const extractBackupAssemblyComponents = formMethods
      .getValues()
      .backupAssemblyComponents.filter(
        (item) =>
          !(
            item.layout_screen_id === assemblyComponent.layout_screen_id &&
            item.screen_section_id === assemblyComponent.screen_section_id
          ),
      );
    if (direction === 'up') {
      const updatedComponents = assemblyComponents
        .map((ac) => {
          if (ac.uid === assemblyComponent.uid) {
            return {
              ...ac,
              rotation_order: ac.rotation_order - 1,
            };
          }
          if (ac.rotation_order === assemblyComponent.rotation_order - 1) {
            return {
              ...ac,
              rotation_order: ac.rotation_order + 1,
            };
          }

          return ac;
        })
        .sort((a, b) => a.rotation_order - b.rotation_order);

      const updatedBackupAssemblyComponentData = backupAssemblyComponents.map(
        (item) => {
          if (item.rotation_order === assemblyComponent.rotation_order) {
            return {
              ...item,
              rotation_order: item.rotation_order - 1,
            };
          } else if (
            item.rotation_order ===
            assemblyComponent.rotation_order - 1
          ) {
            return {
              ...item,
              rotation_order: item.rotation_order + 1,
            };
          }

          return item;
        },
      );

      const newAssemblyComponents = [
        ...extractAssemblyComponents,
        ...updatedComponents,
      ];

      const newbackupAssemblyComponents = [
        ...extractBackupAssemblyComponents,
        ...updatedBackupAssemblyComponentData,
      ];
      formMethods.setValue('assemblyComponents', newAssemblyComponents);
      formMethods.setValue(
        'backupAssemblyComponents',
        newbackupAssemblyComponents,
      );
    } else if (direction === 'down') {
      const updatedComponents = assemblyComponents
        .map((ac) => {
          if (ac.uid === assemblyComponent.uid) {
            return {
              ...ac,
              rotation_order: ac.rotation_order + 1,
            };
          }
          if (ac.rotation_order === assemblyComponent.rotation_order + 1) {
            return {
              ...ac,
              rotation_order: ac.rotation_order - 1,
            };
          }

          return ac;
        })
        .sort((a, b) => a.rotation_order - b.rotation_order);

      const updatedBackupAssemblyComponentData = backupAssemblyComponents.map(
        (item) => {
          if (item.rotation_order === assemblyComponent.rotation_order) {
            return {
              ...item,
              rotation_order: item.rotation_order + 1,
            };
          } else if (
            item.rotation_order ===
            assemblyComponent.rotation_order + 1
          ) {
            return {
              ...item,
              rotation_order: item.rotation_order - 1,
            };
          }

          return item;
        },
      );

      const newAssemblyComponents = [
        ...extractAssemblyComponents,
        ...updatedComponents,
      ];

      const newbackupAssemblyComponents = [
        ...extractBackupAssemblyComponents,
        ...updatedBackupAssemblyComponentData,
      ];

      formMethods.setValue('assemblyComponents', newAssemblyComponents);
      formMethods.setValue(
        'backupAssemblyComponents',
        newbackupAssemblyComponents,
      );
    }
  };

  const onSaved = async (status: IStatus) => {
    setIsSaveDisabled(true);

    try {
      const { hasSectionRotation, hasTakeOver } =
        checkSectionTypes(sectionTypes);
      const newAssemblyComponents = formMethods
        .getValues()
        .assemblyComponents.map((assemblyComponent) => ({
          ...assemblyComponent,
          status: status,
          narrative: (
            narrativeGenerators[COLLECTIONS.ASSEMBLIES][
              USER_ACTIONS.UPDATE
            ] as (assemblyName: string) => string
          )('assemblyComponentId ' + assemblyComponent?.id),
        }));
      const newBackupAssemblyComponents = formMethods
        .getValues()
        .backupAssemblyComponents.map((assemblyComponent) => ({
          ...assemblyComponent,
          status: status,
          narrative: (
            narrativeGenerators[COLLECTIONS.ASSEMBLIES][
              USER_ACTIONS.UPDATE
            ] as (assemblyName: string) => string
          )('assemblyComponentId ' + assemblyComponent?.id),
        }));

      const transactionPayload = {
        updateAssembly: {
          ...formMethods.getValues(),
          ...getUpdatedAssemblyValues(
            status,
            formMethods.getValues().config,
            formMethods.getValues().name,
            formMethods.getValues().variation_parent_id,
            parentAssembly?.config,
            hasSectionRotation,
            hasTakeOver,
          ),
        },
        updateCreateDeleteAssemblyComponents: {
          data: [...newAssemblyComponents, ...newBackupAssemblyComponents],
          assemblyId: toNumber(assemblyId),
          assemblyName: formMethods.getValues().name ?? '',
        },
      };

      await assemblyTransaction.mutateAsync(transactionPayload);

      formMethods.reset(undefined, { keepValues: true });

      const msg = `${formMethods.getValues().name} ${
        lang.assemblySavingMessage.successfully
      } ${
        status === IStatus.DRAFT
          ? lang.assemblySavingMessage.saveAsDraft
          : lang.assemblySavingMessage.saved
      }`;

      expiringInDays &&
        status === IStatus.READY &&
        Snackbar.warning(
          `${formMethods.getValues().name} ${
            lang.componentExpiringIn
          } ${expiringInDays} ${
            expiringInDays === 1 ? lang.day : lang.day + 's'
          }`,
        );

      Snackbar.success(msg);

      if (status === IStatus.READY) {
        saveReadyToUseAssemblyEvent(Number(assemblyId));
        navigate({
          pathname: PATHS.ASSEMBLIES,
        });
      }
    } catch (error) {
      Snackbar.error(lang.failedSaveText);
    } finally {
      setIsSaveDisabled(false);
    }
  };

  const onClickedVariation = (assemblyId?: number) => {
    if (assemblyId) {
      navigate(
        {
          pathname: PATHS.ASSEMBLY_EDIT(assemblyId),
        },
        { replace: true },
      );
    }
  };

  const onClickedConfirmAddVariation = async (variationName: string) => {
    const newCreatedAssembly = await createAssemblyVariation.mutateAsync({
      ...formMethods.getValues(),
      name: variationName,
      variation_parent_id: parentAssembly?.id,
      status: IStatus.DRAFT,
    });

    variationAssemblies.forEach((variationAssembly) => {
      queryClient.refetchQueries({
        queryKey: queryKeys.assemblies.details(variationAssembly.id.toString()),
      });
    });

    onToggleCreateVariationModal();

    navigate({
      pathname: PATHS.ASSEMBLY_EDIT(newCreatedAssembly.id),
    });

    Snackbar.success(`${variationName} ${lang.createdSuccessfully}`);
  };

  const onDeleteAssemblyVariation = () => {
    formMethods.reset();
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const calculateVariationAssemblies = () => {
    const childrenAssemblies: IAssembly[] =
      formMethods.getValues()?.childrenAssemblies ?? [];
    const siblingAssemblies: IAssembly[] =
      formMethods.getValues()?.siblingAssemblies ?? [];

    return concat(childrenAssemblies, siblingAssemblies);
  };

  const {
    productFilters: selectedSectionProductFilters,
    productVariationFilters: selectedSectionProductVariationFilters,
  } = useMemo(() => {
    if (!selectedSection) {
      return { productFilters: [], productVariationFilters: [] };
    }

    const matchedAssemblyComponents = findMatchedAssemblyComponents(
      [...assemblyComponents, ...backupAssemblyComponents],
      selectedSection.layoutScreenId,
      selectedSection.screenSectionId,
      ['rotation_order'],
      parentAssemblyComponents,
    );

    return generateAssemblyProductFilters(
      matchedAssemblyComponents,
      components,
    );
  }, [
    selectedSection,
    assemblyComponents,
    backupAssemblyComponents,
    parentAssemblyComponents,
    components,
  ]);

  useEffect(() => {
    if (
      formMethods.getValues().siblingAssemblies !== undefined &&
      formMethods.getValues().childrenAssemblies !== undefined
    ) {
      variationAssemblies = calculateVariationAssemblies();
    }
  }, [calculateVariationAssemblies, formMethods]);

  const disabledAddVariations =
    !isParentAssemblyReady ||
    variationAssemblies.length >= 10 ||
    userRole === AuthClassification.OWNER_OPERATOR;

  const miniSidebarItems = [
    {
      icon: <AutoAwesomeMosaicIcon />,
      disabledHighlightIconColor: true,
      isSelected: !formMethods.getValues()?.variation_parent_id,
      tooltipMessage: lang.tooltipMessages.originalAssembly,
      onClick: () =>
        onClickedVariation(
          formMethods.getValues()?.variation_parent_id ||
            formMethods.getValues()?.id,
        ),
    },
    ...variationAssemblies.map((item) => ({
      icon: <SubdirectoryArrowRightIcon />,
      tooltipMessage: item.name,
      disabledHighlightIconColor: true,
      isSelected: assemblyId === toString(item?.id),
      onClick: () => onClickedVariation(item?.id),
    })),
    {
      icon: <AddIcon stroke={disabledAddVariations ? 'grey' : 'black'} />,
      onClick: onToggleCreateVariationModal,
      tooltipMessage: !isParentAssemblyReady
        ? 'The original assembly must be saved as ready to use to create a variation.'
        : variationAssemblies.length >= 10
        ? 'Maximum 10 variations'
        : !disabledAddVariations
        ? 'Add variation'
        : '',
      disabled: disabledAddVariations,
      secondaryTooltip: disabledAddVariations,
    },
  ];

  const handleUpdateBackupAssemblyComponents = (
    updatedSectionBackups: IAssemblyComponent[],
  ) => {
    const updatedSectionBackupsMap = new Map(
      updatedSectionBackups.map((item) => [item.uid as string, item]),
    );
    const updatedBackupAssemblyComponents = backupAssemblyComponents.map(
      (item) =>
        updatedSectionBackupsMap.has(item.uid)
          ? (updatedSectionBackupsMap.get(item.uid) as IAssemblyComponent)
          : item,
    );

    formMethods.setValue(
      'backupAssemblyComponents',
      updatedBackupAssemblyComponents,
    );
  };

  const getbackupAssemblyComponentsIds = () => {
    const ids = backupAssemblyComponents?.map((item) => item.component_id);

    return ids;
  };

  return (
    <FormProvider {...formMethods}>
      <AssemblyEditContainer>
        <MiniSidebar items={miniSidebarItems} />

        <SidebarContainer>
          <AssemblyEditPageSidebar
            canEdit={canEdit}
            openSectionDrawer={openSectionDrawer}
            selectedSection={selectedSection}
            onGoBack={onCloseSectionDrawer}
            onChangedAddComponent={onClickedAdd}
            onUpdateRotationDuration={onUpdateRotationDuration}
            onReplacedComponent={onClickedReplace}
            onDeletedAssemblyComponent={onDeletedAssemblyComponent}
            handleComponentSelect={handleComponentSelect}
            onReorderRotation={onReorderRotation}
            onSelectBackupComponent={onSelectBackupComponent}
            parentAssemblyComponent={parentAssemblyComponents}
            childSectionEdit={childSectionEdit}
            handleChildEditSection={handleChildEditSection}
            onDeletedBackupAssemblyComponent={onDeletedBackupAssemblyComponent}
            parentBackupAssemblyComponents={parentBackupAssemblyComponents}
            disableRemoveVariation={disableRemoveVariation}
            setActiveAssemblyJourney={setActiveAssemblyJourney}
            activeAssemblyJourney={activeAssemblyJourney}
            sectionTypes={sectionTypes}
            setIsCodConfirmationOpen={setIsCodConfirmationOpen}
            isCodConfirmationOpen={isCodConfirmationOpen}
            setCustomerState={setCustomerState}
            customerState={customerState}
            variationAssemblies={variationAssemblies}
            assemblyId={Number(assemblyId)}
            onDeleteAssemblyVariation={onDeleteAssemblyVariation}
            handleUpdateBackupAssemblyComponents={
              handleUpdateBackupAssemblyComponents
            }
            productFilters={selectedSectionProductFilters}
            productVariationFilters={selectedSectionProductVariationFilters}
            skipProductsApiOnEmptyFilters={true}
            onChangeDaypart={onChangeDaypart}
            selectedDaypartId={selectedDaypartId}
          />
        </SidebarContainer>
        <AssemblyEditContentContainer ref={containerRef}>
          <HeaderContainer>
            {isAssemblyDetailsLoading ? (
              <Progress />
            ) : (
              <Box flex={1} overflow={'hidden'}>
                {userRole !== AuthClassification.OWNER_OPERATOR && (
                  <Breadcrumbs
                    items={[
                      {
                        label: lang.assemblies,
                        link: PATHS.ASSEMBLIES,
                      },
                      {
                        label: lang.edit,
                      },
                    ]}
                  />
                )}

                <CustomTooltip
                  placement="bottom"
                  title={assemblyName}
                  width={500}
                  overflowWidth={500}>
                  <AssemblyNameText variant="h3">
                    {assemblyName}
                  </AssemblyNameText>
                </CustomTooltip>
              </Box>
            )}
            {isViewOnly ? (
              <ViewOnlyAlert />
            ) : (
              <ActionButtonsContainer spacing={2} direction="row">
                <Button
                  variant="text"
                  onClick={() => {
                    assemblySavedDraftEvent(Number(assemblyId));
                    onSaved(IStatus.DRAFT);
                  }}
                  disabled={!canSaveAsDraft || (!isDirty && isSaveDisabled)}>
                  Save as draft
                </Button>
                <Button
                  variant="contained"
                  onClick={() => onToggleSaveComponentModal()}
                  disabled={!canSaveAsReady}>
                  Save
                </Button>
              </ActionButtonsContainer>
            )}
          </HeaderContainer>
          {hasJourney && <CustomerStateAlert customerState={customerState} />}
          <ScaleContainer enableScaleControls>
            {layouts.map((layout) => (
              <AssemblyLayout key={layout.id}>
                {layout.screens.map((screen, index) => (
                  <FullSizeAssemblyScreen
                    key={screen.name + index}
                    height={screen.height}
                    width={screen.width}
                    selectable={false}
                    type="screen">
                    {screen.sections.map((section, index) => {
                      const disallowedOutdoorSection =
                        isDisallowedOutdoorSection(layout, section);
                      const matchedAssemblyComponents = disallowedOutdoorSection
                        ? (
                            findMatchedGlobalComponents(
                              section.config?.suggestedComponentTypes,
                              globalComponentsData,
                            ) as IAssemblyComponent[]
                          ).filter((item) => item?.component !== undefined)
                        : findMatchedAssemblyComponents(
                            [
                              ...assemblyComponents,
                              ...backupAssemblyComponents,
                            ],
                            screen.layoutScreenId,
                            section.screenSectionId,
                            ['rotation_order'],
                            parentAssemblyComponents,
                          );

                      const {
                        productFilters: sectionProductFilters,
                        productVariationFilters: sectionProductVariationFilters,
                      } = generateAssemblyProductFilters(
                        matchedAssemblyComponents,
                        components,
                      );

                      const canEditAndSelect =
                        canEdit && !disallowedOutdoorSection;

                      return (
                        <FullSizeAssemblySection
                          sectionTypes={sectionTypes}
                          key={`${section.name}-${index}-${selectedDaypartId}-${customerState}`}
                          section={section}
                          customerState={customerState}
                          selectable={canEditAndSelect}
                          showEditIcon={canEditAndSelect}
                          activeAssemblyJourney={activeAssemblyJourney}
                          isCodConfirmationOpen={isCodConfirmationOpen}
                          isDisallowedOutdoorSection={disallowedOutdoorSection}
                          hasDaypartSelected={true}
                          onClick={() => {
                            setChildSectionEdit(false);
                            handleComponentSelect(
                              screen.layoutScreenId ?? 0,
                              section.screenSectionId ?? 0,
                              section.id,
                              section.width,
                              section.height,
                              section.config,
                            );
                          }}
                          showParentIcon={showParentIcon(
                            assemblyComponents,
                            screen.layoutScreenId,
                            section.screenSectionId,
                            formMethods.getValues()
                              ?.variation_parent_id as number,
                          )}
                          showChildIcon={showChildIcon(
                            assemblyComponents,
                            screen.layoutScreenId,
                            section.screenSectionId,
                            formMethods.getValues()
                              ?.variation_parent_id as number,
                          )}
                          assemblyComponents={matchedAssemblyComponents}
                          showSectionBorder={isEmpty(matchedAssemblyComponents)}
                          productFilters={sectionProductFilters}
                          productVariationFilters={
                            sectionProductVariationFilters
                          }
                          skipProductsApiOnEmptyFilters={true}
                          selectedDaypartId={selectedDaypartId}
                        />
                      );
                    })}
                  </FullSizeAssemblyScreen>
                ))}
              </AssemblyLayout>
            ))}
          </ScaleContainer>
        </AssemblyEditContentContainer>
      </AssemblyEditContainer>

      <SimpleDialog
        open={openSaveComponentModal}
        modalSize="sm"
        cancelText={lang.cancel}
        confirmText={lang.save}
        title={lang.savingAssemblyModal.title}
        subTitle={!isExpired ? lang.savingAssemblyModal.subTitle : ''}
        onClose={onToggleSaveComponentModal}
        onClickCancel={onToggleSaveComponentModal}
        onClickConfirm={() => {
          onSaved(IStatus.READY);
        }}
        disabled={isExpired || isSaveDisabled}>
        {isExpired && (
          <Alert severity="warning" icon={<StyledWarningIcon />}>
            {lang.expiredAssembly}
          </Alert>
        )}
      </SimpleDialog>

      {openComponentListModal && (
        <ComponentListModal
          open={openComponentListModal}
          filters={handleFiltersForComponents(
            currentDate,
            formMethods.getValues().layout?.layout_type_id as number,
            componentTypes,
            replaceComponentID.current,
            selectedSection,
            backupAssemblyComponents?.length
              ? getbackupAssemblyComponentsIds()
              : [],
          )}
          onClose={onCloseComponentModal}
          onSelectedComponent={debouncedSelectComponent}
          suggestedComponentTypes={
            selectedSection?.config?.suggestedComponentTypes
          }
        />
      )}

      {openCreateVariationModal && !isViewOnly && (
        <CreateVariationModal
          open={openCreateVariationModal}
          parentAssemblyName={toString(parentAssembly?.name)}
          onClickCancel={onToggleCreateVariationModal}
          onClickConfirm={onClickedConfirmAddVariation}
        />
      )}
    </FormProvider>
  );
};
